import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from './use_api';
import { useNodeInfo } from './use_node_info';

const POLL_INTERVAL_MS = 15_000;

export function useNotifications() {
    const { isAuthenticated, user } = useAuth();
    const api = useApi();
    // Node feature toggle: with notifications off the backend doesn't mount
    // /api/notifications at all, so the 15s poll below would 404 forever.
    const { featureNotifications } = useNodeInfo();
    const [unreadCount, setUnreadCount] = useState(0);
    const [unreadByEntity, setUnreadByEntity] = useState({});
    // Unread app notifications per instance (kind=app): the badge on a node
    // app's nav entry. Kept apart from the message map so the Messages
    // badge still counts only messages.
    const [unreadByApp, setUnreadByApp] = useState({});
    const [unreadMessagesTotal, setUnreadMessagesTotal] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const pollRef = useRef(null);

    const userId = user?.id;

    const fetchUnreadCount = useCallback(() => {
        if (!userId) return;
        api.get('/api/notifications/unread-count', { params: { user_id: userId } })
            .then(data => { if (data) setUnreadCount(data.unread_count); })
            .catch(() => {});
    }, [userId, api]);

    const fetchUnreadByEntity = useCallback(() => {
        if (!userId) return;
        api.get('/api/notifications/unread-by-entity', { params: { user_id: userId, kind: 'message' } })
            .then(data => {
                if (!data) return;
                setUnreadByEntity(data.by_entity || {});
                setUnreadMessagesTotal(data.total || 0);
            })
            .catch(() => {});
    }, [userId, api]);

    const fetchUnreadByApp = useCallback(() => {
        if (!userId) return;
        api.get('/api/notifications/unread-by-entity', { params: { user_id: userId, kind: 'app' } })
            .then(data => { if (data) setUnreadByApp(data.by_entity || {}); })
            .catch(() => {});
    }, [userId, api]);

    const refreshAll = useCallback(() => {
        fetchUnreadCount();
        fetchUnreadByEntity();
        fetchUnreadByApp();
    }, [fetchUnreadCount, fetchUnreadByEntity, fetchUnreadByApp]);

    useEffect(() => {
        if (!isAuthenticated || !userId || !featureNotifications) return;
        refreshAll();
        pollRef.current = setInterval(refreshAll, POLL_INTERVAL_MS);
        return () => clearInterval(pollRef.current);
    }, [isAuthenticated, userId, refreshAll, featureNotifications]);

    const fetchNotifications = useCallback(() => {
        if (!userId) return;
        api.get('/api/notifications', { params: { user_id: userId, limit: 20 } })
            .then(data => setNotifications(data))
            .catch(() => {});
    }, [userId, api]);

    const openDropdown = useCallback(() => {
        setDropdownOpen(true);
        fetchNotifications();
    }, [fetchNotifications]);

    const closeDropdown = useCallback(() => {
        setDropdownOpen(false);
    }, []);

    const markEntityRead = useCallback((entityId) => {
        if (!userId) return;
        // Optimistic update so badges clear instantly instead of waiting for the next poll.
        const cleared = unreadByEntity[entityId] || 0;
        if (cleared > 0) {
            setUnreadByEntity(prev => {
                if (!prev[entityId]) return prev;
                const next = { ...prev };
                delete next[entityId];
                return next;
            });
            setUnreadMessagesTotal(t => Math.max(0, t - cleared));
        }
        setUnreadByApp(prev => {
            if (!prev[entityId]) return prev;
            const next = { ...prev };
            delete next[entityId];
            return next;
        });
        // Reconcile regardless of outcome (matches prior behaviour: the .then ran
        // on success or HTTP-error; refreshAll re-syncs counts from the server).
        api.post(`/api/notifications/${entityId}/mark-read`, undefined, { params: { user_id: userId } })
            .catch(() => {})
            .then(() => {
                setNotifications(prev => prev.map(n => n.entity_id === entityId ? { ...n, is_read: true } : n));
                refreshAll();
            });
    }, [userId, api, refreshAll, unreadByEntity]);

    const markAllRead = useCallback(() => {
        if (!userId) return;
        api.post('/api/notifications/mark-all-read', undefined, { params: { user_id: userId } })
            .catch(() => {})
            .then(() => {
                setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                setUnreadCount(0);
                setUnreadByEntity({});
                setUnreadByApp({});
                setUnreadMessagesTotal(0);
            });
    }, [userId, api]);

    const setPreference = useCallback((entityId, enabled) => {
        if (!userId) return Promise.resolve();
        // Resolves with the saved prefs on success, rejects (throws ApiError) on
        // failure — callers rely on the rejection to roll back their optimistic toggle.
        return api.put(`/api/notifications/preferences/${entityId}`, { enabled }, { params: { user_id: userId } });
    }, [userId, api]);

    const fetchPreferences = useCallback(() => {
        if (!userId) return Promise.resolve({});
        return api.get('/api/notifications/preferences', { params: { user_id: userId } })
            .then(prefs => {
                const map = {};
                prefs.forEach(p => { map[p.entity_id] = p.enabled; });
                return map;
            })
            .catch(() => ({}));
    }, [userId, api]);

    return {
        unreadCount,
        unreadByEntity,
        unreadByApp,
        unreadMessagesTotal,
        notifications,
        dropdownOpen,
        openDropdown,
        closeDropdown,
        markEntityRead,
        markAllRead,
        setPreference,
        fetchPreferences,
        refetchUnread: refreshAll,
        featureNotifications,
    };
}
